import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { LenderUser } from 'app/models/dto/lender-user.model';
import { LenderUserState } from 'app/store/lender-user/lender-user/lender-user.state';
import { OfferOption } from 'ng2-app/models/dto/offer-option/offer-option.model';
import {
  DeleteOfferOption,
  PatchOfferOptionStatus,
} from 'ng2-app/store/offers/offer-options.actions';
import { OffersService } from 'app/services/offers/offers.service';
import { MatDialog } from '@angular/material/dialog';
import { OfferOptionEditDialogComponent } from 'ng2-app/offer-option/offer-option-edit-modal/offer-option-edit-dialog.component';
import { OfferOptionPublishDialogComponent } from 'ng2-app/offer-option/offer-option-publish-modal/offer-option-publish-dialog.component';
import { Observable, combineLatest } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ConfirmationDialogComponent } from 'ng2-app/modules/confirmation-dialog/confirmation-dialog.component';
import { OfferOptionSnackBarType } from 'ng2-app/offer-option/offer-option-snack-bar-data/offer-option-snack-bar-data.interface';
import { OfferOptionSnackBarDataComponent } from 'ng2-app/offer-option/offer-option-snack-bar-data/offer-option-snack-bar-data.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OfferOptionsState } from 'ng2-app/store/offers/offer-options.state';
import { DealState } from 'ng2-app/store/deal/deal/deal.state';
import { OfferState } from 'ng2-app/store/offers/offer.state';
import { SaveDealStatus } from 'ng2-app/store/deal/deal/deal.actions';

@Component({
  selector: 'offer-option-more-menu',
  templateUrl: './offer-option-more-menu.component.html',
  styleUrls: ['./offer-option-more-menu.component.scss'],
})
export class OfferOptionMoreMenuComponent implements OnInit {
  @Input() option: OfferOption;
  @Input() optionType: string;
  @Input() optionParentName: string;

  @Output() offerOptionUpdated = new EventEmitter<object>();

  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;

  optionLoanCategoryType: string;
  canPublishOption: boolean;
  canCreateContractRequest: boolean;
  canEditOption: boolean;
  canDeleteOption: boolean;

  @ViewChild('offerOptionEditModal') private offerOptionEditModal: any;
  @ViewChild('offerOptionDeleteModal') private offerOptionDeleteModal: any;
  @ViewChild('offerOptionPublishModal') private offerOptionPublishModal: any;

  constructor(
    private store: Store,
    private offersService: OffersService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) { }

  async ngOnInit() {
    this.canCreateContractRequest = true;

    const invalidStagesForCRCreate = ['application','funded','inactive'];
    const invalidStatusesForCRCreate = ['awaitingResponse','declined'];

    this.optionLoanCategoryType = this.option?.deal?.loan_product?.category?.type || '';

    this.canCreateContractRequest = !((invalidStagesForCRCreate.some(stage => stage === this.option.deal?.stage)) ||
        (invalidStatusesForCRCreate.some(status => status === this.option.deal?.status)) ||
        !!this.option.deal?.['contract_request']
        );

        this.lenderUser$.pipe(
        take(1)).subscribe(
        (user) => {
        this.canPublishOption = user?.permissions?.includes('canPublishOffersToPortal');
      });

    this.canDeleteOption = (
      !this.option.offer?.accepted
      && this.option.deal?.stage !== 'inactive'
      && this.option.deal?.stage !== 'funded'
    );

    this.lenderUser$.pipe(take(1)).subscribe((user) => {
      this.canEditOption = (
        user?.permissions?.includes('editOffers')
        && (!user?.roles?.includes('Account Executive') || (this.option.createdByUserId === user?.id && this.option.status === 'draft'))
      );
      this.canPublishOption = user?.permissions?.includes(
        'canPublishOffersToPortal'
      );
    });
  }

  /**
   * Send OfferOption data over to the Contract Request component for submission
   */
  requestContract() {
    this.offersService.emitOfferOption(this.option);
  }

  changeStatus(status) {
    this.store.dispatch(
      new PatchOfferOptionStatus(this.option.dealId, this.option.id, status)
    );
  }

  editOption() {
    const dialogRef = this.dialog.open(OfferOptionEditDialogComponent, {
      width: '900px',
      data: {
        optionType: this.optionType,
        option: this.option,
        optionParent: this.getOptionParent(this.option),
        optionParentName: this.optionParentName,
      }
    });
    dialogRef.componentInstance.offerOptionUpdated.pipe(
      take(1)
    ).subscribe( (updatedOfferData) => this.handleOfferOptionUpdated(updatedOfferData) );
  }

  handleOfferOptionUpdated(updatedOfferData){
    this.offerOptionUpdated.emit(updatedOfferData);

    this.option.termSet.term = updatedOfferData.term;
    this.option.termSet.paymentFrequency = updatedOfferData.paymentFrequency;
    this.option.termSet.points = updatedOfferData.points;
    this.option.termSet.paymentCount = updatedOfferData.paymentCount;
    this.option.termSet.paymentAmount = updatedOfferData.paymentAmount;
    this.option.termSet.disbursement = updatedOfferData.disbursement;
    this.option.termSet.commission = updatedOfferData.commission;

    switch (this.optionLoanCategoryType) {
      case 'loc':
        this.option.termSet.creditLimit = updatedOfferData.creditLimit;
        this.option.termSet.initialDrawAmount = updatedOfferData.initialDrawAmount;
        this.option.termSet.periodicRate = updatedOfferData.periodicRate;
        this.option.termSet.periodsPerYear = updatedOfferData.periodsPerYear;
        this.option.termSet.annualInterestRate = updatedOfferData.annualInterestRate;
        this.option.termSet.apr = updatedOfferData.apr;
        this.option.termSet.drawFeePercent = updatedOfferData.drawFeePercent;
        this.option.termSet.drawFeeAmount = updatedOfferData.drawFeeAmount;
        break;
      case 'term':
        this.option.termSet.amount = updatedOfferData.amount;
        this.option.termSet.periodicRate = updatedOfferData.periodicRate;
        this.option.termSet.periodsPerYear = updatedOfferData.periodsPerYear;
        this.option.termSet.annualInterestRate = updatedOfferData.annualInterestRate;
        this.option.termSet.apr = updatedOfferData.apr;
        this.option.termSet.originationFeePercent = updatedOfferData.originationFeePercent;
        this.option.termSet.originationFeeAmount = updatedOfferData.originationFeeAmount;
        break;
      case 'ach':
      default:
        this.option.termSet.amount = updatedOfferData.amount;
        this.option.termSet.originationFeePercent = updatedOfferData.originationFeePercent;
        this.option.termSet.factorRate = updatedOfferData.factorRate;
        this.option.termSet.payback = updatedOfferData.payback;
        this.option.termSet.originationFeeAmount = updatedOfferData.originationFeeAmount;
    }
  }

  confirmDelete() {
    const confirmationPromptText = this.option.status === 'optionToBorrower' ?
      'Are you sure you want to delete this offer? This will also remove the offer from borrower portal.' :
      'Are you sure you want to delete this offer?';
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        showHeader: true,
        headerText: 'Delete Option',
        confirmationPromptText: confirmationPromptText,
        showCancelButton: true,
        confirmButtonText: 'Delete',
      },
    });
    dialogRef.componentInstance.confirm.pipe(take(1)).subscribe(() => {
      this.store.dispatch(new DeleteOfferOption(this.option.dealId, this.option.id)).pipe(
        take(1)
      ).subscribe(() => {
        this.downgradeDealStatus(this.option.dealId);
      });
      dialogRef.close();
    });
  }

  getOptionParent(option: OfferOption) {
    if (this.optionType === 'approval') {
      return option.approval;
    }
    if (this.optionType === 'offer') {
      return option.offer;
    }
    return [];
  }

  openPublishConfirmationDialog() {
    this.dialog.open(OfferOptionPublishDialogComponent, {
      width: '580px',
      data: {
        optionType: this.optionType,
        option: this.option,
      },
    });
  }

  openRemoveFromPortalDialog() {
    const offerOption = this.option;
    const optionType = this.optionType;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        showHeader: true,
        headerText: 'Remove option from portal',
        confirmationPromptText:
          'Once removed the borrower will no longer be able to view this option and it will revert back to a draft in pipeline.',
        showCancelButton: true,
        confirmButtonText: 'Remove'
      }
    });
    dialogRef.componentInstance.confirm.pipe(take(1)).subscribe(() => {
      this.store.dispatch(new PatchOfferOptionStatus(offerOption.dealId, offerOption.id, 'draft', true)).pipe(
        take(1)
      ).subscribe(() => {
        this.openOfferSnackBar(offerOption, optionType, true, OfferOptionSnackBarType.REMOVE_FROM_PORTAL);

        this.downgradeDealStatus(offerOption.dealId);
      });
      dialogRef.close();
    });
  }

  downgradeDealStatus(dealId) {
    const deal$ = this.store.select(DealState.deals).pipe(map((deals) => deals.find((deal) => deal.id === dealId)));
    const dealOptions$ = this.store.select(OfferOptionsState.dealOptions).pipe(map(filterFn => filterFn(dealId)));
    const dealOffers$ = this.store.select(OfferState.dealOffers).pipe(map(filterFn => filterFn(dealId, '')));

    combineLatest([
      deal$,
      dealOptions$,
      dealOffers$,
    ]).pipe(take(1))
      .subscribe(([deal, options, offers]) => {
        if (deal?.stage !== 'underwriting' || deal?.status !== 'offerToBorrower') {
          return;
        }
        const hasPublishedOptions = (options || []).some((option) => (
          Boolean(option.publishedToBorrower) && !['draft', 'declined'].includes(option.status)
        ));
        const hasPublishedOffers = (offers || []).some((offer) => Boolean(offer.publishedToBorrower));

        if (hasPublishedOptions || hasPublishedOffers) {
          return;
        }

        this.store.dispatch(new SaveDealStatus(deal.id, 'underwriting', 'offerReceived', '', [], []))
          .subscribe();
      });
  }

  openOfferSnackBar(
    offerOption: OfferOption,
    optionType: string,
    isSuccess: boolean,
    type: OfferOptionSnackBarType
  ) {
    this.snackBar.openFromComponent(OfferOptionSnackBarDataComponent, {
      data: {
        offerOption,
        optionType,
        isSuccess: isSuccess,
        type,
      },
      duration: 3000,
    });
  }
}
